import {
    ApiGnomeSnapshotSchema,
    ApiGnomeLoadoutSlotSchema,
    GNOME_FACIAL_EXPRESSION_IDS,
    GNOME_SKIN_COLOR_IDS,
} from "@bridge-applications/pb-types";
import { z } from "zod";

export const GNOME_FACIAL_EXPRESSIONS = GNOME_FACIAL_EXPRESSION_IDS;

export const GnomeFacialExpressionSchema = z.enum(GNOME_FACIAL_EXPRESSIONS);

export type GnomeFacialExpression = z.infer<typeof GnomeFacialExpressionSchema>;

export const GnomeEquipmentItemIdSchema = z.enum([
    "headwear-006",
    "headwear-010",
    "headwear-011",
    "headwear-012",
    "headwear-013",
    "headwear-014",
    "headwear-015",
    "headwear-016",
    "headwear-017",
    "headwear-018",
    "beard-001",
    "glasses-001",
    "glasses-002",
    "glasses-003",
    "shirt-002",
    "shirt-003",
    "shirt-005",
    "shirt-006",
    "shirt-007",
    "shirt-008",
    "shirt-009",
    "shirt-010",
    "shirt-011",
    "shirt-012",
    "shirt-013",
    "shirt-014",
    "shirt-015",
    "shirt-016",
    "pants-001",
    "pants-002",
    "pants-004",
    "pants-005",
    "pants-006",
    "pants-007",
    "pants-008",
    "pants-009",
    "pants-010",
    "pants-011",
    "footwear-001",
    "footwear-002",
    "footwear-004",
    "footwear-005",
    "footwear-006",
    "footwear-007",
    "footwear-008",
    "footwear-009",
]);

export type GnomeEquipmentItemId = z.infer<typeof GnomeEquipmentItemIdSchema>;

export const GnomeSkinColorIdSchema = z.enum(GNOME_SKIN_COLOR_IDS);

export const GnomeEquippedItemIdsSchema = z.object({
    headwear: z
        .enum([
            "headwear-006",
            "headwear-010",
            "headwear-011",
            "headwear-012",
            "headwear-013",
            "headwear-014",
            "headwear-015",
            "headwear-016",
            "headwear-017",
            "headwear-018",
        ])
        .nullable(),
    beard: z.enum(["beard-001"]).nullable(),
    glasses: z.enum(["glasses-001", "glasses-002", "glasses-003"]).nullable(),
    shirt: z
        .enum([
            "shirt-002",
            "shirt-003",
            "shirt-005",
            "shirt-006",
            "shirt-007",
            "shirt-008",
            "shirt-009",
            "shirt-010",
            "shirt-011",
            "shirt-012",
            "shirt-013",
            "shirt-014",
            "shirt-015",
            "shirt-016",
        ])
        .nullable(),
    pants: z
        .enum([
            "pants-001",
            "pants-002",
            "pants-004",
            "pants-005",
            "pants-006",
            "pants-007",
            "pants-008",
            "pants-009",
            "pants-010",
            "pants-011",
        ])
        .nullable(),
    footwear: z
        .enum([
            "footwear-001",
            "footwear-002",
            "footwear-004",
            "footwear-005",
            "footwear-006",
            "footwear-007",
            "footwear-008",
            "footwear-009",
        ])
        .nullable(),
});

export type GnomeEquippedItemIds = z.infer<typeof GnomeEquippedItemIdsSchema>;

export const GnomeCustomizationSliceSchema = z.object({
    snapshotUserId: z.uuid().nullable().default(null),
    snapshotGeneration: z.number().int().nonnegative().default(0),
    snapshotFetchedAt: z.number().nullable().default(null),
    backgroundReadStatus: z
        .enum(["idle", "loading", "ready", "absent", "failed"])
        .default("idle"),
    refreshRequestedRevision: z.number().int().nonnegative().default(0),
    /** Local authoring session only; never sent to or merged into backend inventory. */
    localEarnedItemIds: z.array(GnomeEquipmentItemIdSchema).default([]),
    localEquipmentReward: z
        .object({
            previousEquipment: GnomeEquippedItemIdsSchema,
            equippedEquipment: GnomeEquippedItemIdsSchema,
        })
        .nullable()
        .default(null),
    /** Presentation only; ownership and loadout remain in the server snapshot. */
    backendEquipmentReward: z
        .object({
            previousLoadout: z.array(ApiGnomeLoadoutSlotSchema),
            equippedLoadout: z.array(ApiGnomeLoadoutSlotSchema),
        })
        .nullable()
        .default(null),
    /** Shared across Career/changing-room runtime owners; refreshes cannot erase it. */
    pendingMutationCount: z.number().int().nonnegative().default(0),
    syncStatus: z.enum(["client-only", "loading", "ready", "saving", "error"]),
    syncError: z.string().nullable(),
    snapshot: ApiGnomeSnapshotSchema.nullable(),
    displayName: z.string().trim().min(1).max(48).nullable(),
    facialExpression: GnomeFacialExpressionSchema,
    skinColorId: GnomeSkinColorIdSchema,
    equippedItemIds: GnomeEquippedItemIdsSchema,
});

export type GnomeCustomizationSlice = z.infer<
    typeof GnomeCustomizationSliceSchema
>;
